/**
 * Estado global de la PWA.
 *
 * Deliberadamente pequeño: sesión, paquete del instrumento, asignaciones y
 * estado de la cola. Todo lo que debe sobrevivir a un cierre de la app vive en
 * IndexedDB (db.ts), no aquí; este store es sólo la vista en memoria.
 */

import { create } from 'zustand';

import {
  api,
  session,
  type Assignment,
  type DispositionDef,
  type FieldPackage,
} from './api/client';
import { CACHE_KEYS, db, getCache, pendingCounts, putCache, wipeDevice } from './db/db';
import { prefetchAssignedGeometry, type PrefetchReport } from './engine/offline-geo';
import { refreshConnection } from './telemetry/dataUsage';

export interface Operator {
  id: string;
  code: string;
  name: string;
  role: string;
  mustChangePassword: boolean;
  birthdayRule: 'proximo' | 'ultimo' | null;
}

export interface PendingSummary {
  interviews: number;
  households: number;
  rejected: number;
  drafts: number;
}

interface AppState {
  /* ----------------------------- Sesión ----------------------------- */
  operator: Operator | null;
  isAuthenticated: boolean;
  isBootstrapping: boolean;

  /* ------------------------- Datos de campo ------------------------- */
  fieldPackage: FieldPackage | null;
  assignments: Assignment[];
  dispositions: DispositionDef[];
  timeSlots: { code: string; label: string }[];
  outOfPolygonReasons: { code: string; label: string; description: string }[];
  /** Cuándo se descargó el paquete, para avisar si está muy viejo. */
  packageFetchedAt: number | null;

  /* ---------------------------- Conexión ---------------------------- */
  isOnline: boolean;
  pending: PendingSummary;
  lastSyncAt: number | null;
  prefetchReport: PrefetchReport | null;

  /* ---------------------------- Acciones ---------------------------- */
  login: (identifier: string, password: string) => Promise<void>;
  logout: (force?: boolean) => Promise<{ ok: boolean; pending?: number }>;
  bootstrap: () => Promise<void>;
  loadFieldData: (options?: { prefetchGeometry?: boolean }) => Promise<void>;
  refreshPending: () => Promise<void>;
  setOnline: (online: boolean) => void;
  markSynced: () => void;
  markPasswordChanged: () => void;
}

export const useApp = create<AppState>((set, get) => ({
  operator: null,
  isAuthenticated: false,
  isBootstrapping: true,

  fieldPackage: null,
  assignments: [],
  dispositions: [],
  timeSlots: [],
  outOfPolygonReasons: [],
  packageFetchedAt: null,

  isOnline: navigator.onLine,
  pending: { interviews: 0, households: 0, rejected: 0, drafts: 0 },
  lastSyncAt: null,
  prefetchReport: null,

  async login(identifier, password) {
    const result = await api.login(identifier, password);
    session.set(result.accessToken, result.refreshToken);
    const operator: Operator = {
      id: result.user.id,
      code: result.user.code,
      name: result.user.name,
      role: result.user.role,
      mustChangePassword: result.user.mustChangePassword,
      birthdayRule: result.user.birthdayRule,
    };
    await putCache(CACHE_KEYS.profile, operator);
    set({ operator, isAuthenticated: true });

    // El paquete y la cartografía se descargan sólo cuando ya no hay cambio de
    // contraseña pendiente: el API bloquea el resto de las rutas hasta
    // entonces, así que pedirlas antes fallaría con 403.
    if (!operator.mustChangePassword) {
      await get().loadFieldData({ prefetchGeometry: true });
      // La detección de red arranca con la aplicación, ANTES de que haya
      // sesión, así que su primer intento se va en un 401. Aquí ya hay token:
      // sin este refresco la tarjeta diría «identificando» hasta el siguiente
      // ciclo del temporizador.
      void refreshConnection();
    }
  },

  async logout(force = false) {
    const result = await wipeDevice({ force });
    if (!result.wiped) {
      return { ok: false, pending: result.pending };
    }
    // Cerrar la sesión en el servidor es deseable pero no indispensable: si no
    // hay red, el token local ya se borró y la sesión caducará por sí sola.
    try {
      await api.logout();
    } catch {
      /* sin red: se cierra localmente */
    }
    session.clear();
    set({
      operator: null,
      isAuthenticated: false,
      fieldPackage: null,
      assignments: [],
      packageFetchedAt: null,
      prefetchReport: null,
      pending: { interviews: 0, households: 0, rejected: 0, drafts: 0 },
    });
    return { ok: true };
  },

  async bootstrap() {
    set({ isBootstrapping: true });

    const cachedProfile = await getCache<Operator>(CACHE_KEYS.profile);
    const hasToken = Boolean(session.accessToken);

    if (!hasToken) {
      set({ isBootstrapping: false, isAuthenticated: false, operator: null });
      return;
    }

    // Se asume autenticado con lo cacheado para que la app abra sin red; si hay
    // red se confirma contra el servidor.
    if (cachedProfile) {
      set({ operator: cachedProfile.value, isAuthenticated: true });
    }

    if (navigator.onLine) {
      try {
        const me = await api.me();
        const operator: Operator = {
          id: me.id,
          code: me.code,
          name: me.name,
          role: me.role,
          mustChangePassword: me.mustChangePassword,
          birthdayRule: me.birthdayRule,
        };
        await putCache(CACHE_KEYS.profile, operator);
        set({ operator, isAuthenticated: true });
      } catch {
        // Token inválido con red disponible: no hay sesión.
        if (!cachedProfile) {
          session.clear();
          set({ isAuthenticated: false, operator: null });
        }
      }
    }

    await get().loadFieldData();
    await get().refreshPending();
    set({ isBootstrapping: false });
  },

  /**
   * Carga el paquete de campo. Intenta el servidor y cae a la caché local, de
   * modo que la app funcione igual al inicio de la jornada con WiFi y a media
   * mañana sin señal.
   */
  async loadFieldData(options = {}) {
    const operator = get().operator;
    if (!operator || operator.mustChangePassword) return;

    const fromCache = async () => {
      const [pkg, assignments, dispositions, reasons] = await Promise.all([
        getCache<FieldPackage>(CACHE_KEYS.fieldPackage),
        getCache<Assignment[]>(CACHE_KEYS.assignments),
        getCache<{ dispositions: DispositionDef[]; timeSlots: { code: string; label: string }[] }>(
          CACHE_KEYS.dispositions,
        ),
        getCache<{ code: string; label: string; description: string }[]>(
          CACHE_KEYS.outOfPolygonReasons,
        ),
      ]);
      set({
        fieldPackage: pkg?.value ?? null,
        assignments: assignments?.value ?? [],
        dispositions: dispositions?.value.dispositions ?? [],
        timeSlots: dispositions?.value.timeSlots ?? [],
        outOfPolygonReasons: reasons?.value ?? [],
        packageFetchedAt: pkg?.fetchedAt ?? null,
      });
    };

    if (!navigator.onLine) {
      await fromCache();
      return;
    }

    try {
      const [pkg, assignments, dispositions, reasons] = await Promise.all([
        api.fieldPackage(),
        api.myAssignments(),
        api.dispositions(),
        api.outOfPolygonReasons(),
      ]);

      await Promise.all([
        putCache(CACHE_KEYS.fieldPackage, pkg),
        putCache(CACHE_KEYS.assignments, assignments),
        putCache(CACHE_KEYS.dispositions, dispositions),
        putCache(CACHE_KEYS.outOfPolygonReasons, reasons),
      ]);

      set({
        fieldPackage: pkg,
        assignments,
        dispositions: dispositions.dispositions,
        timeSlots: dispositions.timeSlots,
        outOfPolygonReasons: reasons,
        packageFetchedAt: Date.now(),
      });

      if (options.prefetchGeometry && assignments.length > 0) {
        const report = await prefetchAssignedGeometry(assignments);
        set({ prefetchReport: report });
      }
    } catch {
      await fromCache();
    }
  },

  async refreshPending() {
    set({ pending: await pendingCounts() });
  },

  setOnline(online) {
    set({ isOnline: online });
  },

  markSynced() {
    set({ lastSyncAt: Date.now() });
    void get().refreshPending();
  },

  markPasswordChanged() {
    const operator = get().operator;
    if (!operator) return;
    const updated = { ...operator, mustChangePassword: false };
    void putCache(CACHE_KEYS.profile, updated);
    set({ operator: updated });
    void get().loadFieldData({ prefetchGeometry: true });
  },
}));

/* -------------------------------------------------------------------------- */
/*                            Vigilancia de conexión                          */
/* -------------------------------------------------------------------------- */

export function watchConnectivity(): () => void {
  const update = () => useApp.getState().setOnline(navigator.onLine);
  window.addEventListener('online', update);
  window.addEventListener('offline', update);
  return () => {
    window.removeEventListener('online', update);
    window.removeEventListener('offline', update);
  };
}

/** Borradores sin terminar, para ofrecer retomarlos. */
export async function unfinishedDrafts() {
  return db.drafts.filter((d) => d.finishedAt === null).toArray();
}
